import { useState } from 'react';
import {
  apiConnectSearchConsole,
  apiDisconnectSearchConsole,
  apiGetSearchConsole,
  apiGetSearchPerformance,
  apiSubmitSitemap,
  apiVerifySearchConsole,
  type SearchConsoleStatus,
  type SearchPerformance,
} from '~/lib/api/marketing';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  CAPTION,
  ErrorBanner,
  LoadError,
  Pill,
  Section,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import {
  apiErrorMessage,
  compactNumber,
  connectReturnUrl,
  ctrLabel,
  formatDate,
  pagePath,
  positionLabel,
  searchConsoleStage,
} from './marketing-model';

type Busy = 'connect' | 'verify' | 'sitemap' | 'disconnect' | null;

export function SearchConsoleView({ appId }: { appId: string }) {
  const status = useResource(() => apiGetSearchConsole(appId), appId);

  if (status.loading && !status.data) {
    return (
      <Section title="Google Search Console" icon="i-ph:magnifying-glass">
        <SkeletonCard lines={3} />
      </Section>
    );
  }

  if (!status.data) {
    return (
      <Section title="Google Search Console" icon="i-ph:magnifying-glass">
        <LoadError message="Could not load Search Console." onRetry={() => void status.reload()} />
      </Section>
    );
  }

  return (
    <>
      <StatusCard appId={appId} status={status.data} onStatus={status.setData} />
      {status.data.verified && (
        <PerformanceSection appId={appId} siteUrl={status.data.siteUrl} key={status.data.siteUrl ?? ''} />
      )}
    </>
  );
}

function StatusCard({
  appId,
  status,
  onStatus,
}: {
  appId: string;
  status: SearchConsoleStatus;
  onStatus: (next: SearchConsoleStatus) => void;
}) {
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const stage = searchConsoleStage(status);

  const run = async (action: Exclude<Busy, null>) => {
    setBusy(action);
    setError(null);

    try {
      if (action === 'connect') {
        const { url } = await apiConnectSearchConsole(appId, connectReturnUrl(window.location.href));
        window.location.assign(url);

        return; // leaving for Google; keep the spinner
      }

      const call = {
        verify: apiVerifySearchConsole,
        sitemap: apiSubmitSitemap,
        disconnect: apiDisconnectSearchConsole,
      };
      onStatus(await call[action](appId));
      setConfirming(false);
    } catch (err) {
      setError(
        apiErrorMessage(
          err,
          action === 'connect'
            ? 'Could not start Google sign-in. Please try again.'
            : action === 'verify'
              ? 'Could not verify the site.'
              : action === 'sitemap'
                ? 'Could not submit the sitemap.'
                : 'Could not disconnect.',
        ),
      );
    }

    setBusy(null);
  };

  return (
    <Section
      title="Google Search Console"
      icon="i-ph:magnifying-glass"
      aside={
        stage === 'verified' ? (
          <Pill tone="success" label="Verified" />
        ) : stage === 'unverified' ? (
          <Pill tone="warning" label="Pending" />
        ) : undefined
      }
    >
      <div className={classNames(CARD, 'space-y-3 p-4')}>
        {stage === 'disconnected' ? (
          <>
            <p className="text-[12px] leading-relaxed text-secondary">
              Connect Google to verify your site automatically, submit its sitemap, and see the searches people find you
              with.
            </p>
            <p className="text-[11px] leading-relaxed text-muted">
              Verification needs one publish after connecting: Dunea adds Google's verification tag to your site when
              you publish.
            </p>
            {status.lastError && <ErrorBanner message={status.lastError} />}
            {error && <ErrorBanner message={error} />}
            <button
              onClick={() => void run('connect')}
              disabled={busy !== null}
              className="ui-btn-accent !px-4 !py-2 !text-[12px]"
            >
              {busy === 'connect' ? <Spinner /> : <div className="i-ph:google-logo text-[13px]" />}
              Connect Google
            </button>
          </>
        ) : (
          <>
            <dl className="space-y-2 text-[12px]">
              <Row label="Google account">{status.googleEmail ?? 'Connected'}</Row>
              <Row label="Site">
                {status.siteUrl ? (
                  <span className="font-geist-mono text-[11px]">{status.siteUrl}</span>
                ) : (
                  <span className="text-muted">Publish your site first</span>
                )}
              </Row>
              <Row label="Ownership">
                {status.verified ? (
                  <span className="inline-flex items-center gap-1 text-success">
                    <div className="i-ph:check-bold text-[12px]" />
                    Verified
                  </span>
                ) : (
                  <span className="text-warning">Pending</span>
                )}
              </Row>
              <Row label="Sitemap">
                {status.sitemapSubmittedAt ? (
                  `Submitted ${formatDate(status.sitemapSubmittedAt)}`
                ) : (
                  <span className="text-muted">Not submitted</span>
                )}
              </Row>
            </dl>

            {!status.verified && (
              <p className="text-[11px] leading-relaxed text-muted">
                Publish your site after connecting so Google can see the verification tag, then press Verify. Dunea also
                retries on its own.
              </p>
            )}

            {status.lastError && <ErrorBanner message={status.lastError} />}
            {error && <ErrorBanner message={error} />}

            <div className="flex flex-wrap items-center gap-2 border-t border-hairline pt-3">
              {!status.verified && (
                <button
                  onClick={() => void run('verify')}
                  disabled={busy !== null}
                  className="ui-btn-accent !px-4 !py-2 !text-[12px]"
                >
                  {busy === 'verify' ? <Spinner /> : <div className="i-ph:seal-check text-[13px]" />}
                  Verify
                </button>
              )}
              <button
                onClick={() => void run('sitemap')}
                disabled={busy !== null || !status.verified}
                title={status.verified ? undefined : 'Verify the site first'}
                className={BTN_SECONDARY}
              >
                {busy === 'sitemap' ? <Spinner /> : <div className="i-ph:tree-structure text-[13px]" />}
                {status.sitemapSubmittedAt ? 'Resubmit sitemap' : 'Submit sitemap'}
              </button>

              <div className="ml-auto flex items-center gap-1.5">
                {confirming ? (
                  <>
                    <span className="text-[11px] text-muted">Disconnect Google?</span>
                    <button
                      onClick={() => void run('disconnect')}
                      disabled={busy !== null}
                      className={classNames(BTN_GHOST, '!text-danger')}
                    >
                      {busy === 'disconnect' && <Spinner />}
                      Disconnect
                    </button>
                    <button onClick={() => setConfirming(false)} disabled={busy !== null} className={BTN_GHOST}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <button onClick={() => setConfirming(true)} disabled={busy !== null} className={BTN_GHOST}>
                    Disconnect
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd className="min-w-0 truncate text-right text-primary">{children}</dd>
    </div>
  );
}

function PerformanceSection({ appId, siteUrl }: { appId: string; siteUrl: string | null }) {
  const perf = useResource(() => apiGetSearchPerformance(appId, 28), appId);

  return (
    <Section title="Search performance, last 28 days" icon="i-ph:trend-up">
      {perf.loading && !perf.data ? (
        <SkeletonCard lines={4} />
      ) : !perf.data ? (
        <LoadError message="Could not load search performance." onRetry={() => void perf.reload()} />
      ) : (
        <PerformanceBody perf={perf.data} siteUrl={siteUrl} />
      )}
    </Section>
  );
}

function PerformanceBody({ perf, siteUrl }: { perf: SearchPerformance; siteUrl: string | null }) {
  const metrics = [
    ['Clicks', compactNumber(perf.totals.clicks)],
    ['Impressions', compactNumber(perf.totals.impressions)],
    ['CTR', ctrLabel(perf.totals.ctr)],
    ['Avg position', positionLabel(perf.totals.position)],
  ] as const;

  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {metrics.map(([label, value]) => (
          <div key={label} className={classNames(CARD, 'px-3 py-2.5')}>
            <div className={CAPTION}>{label}</div>
            <div className="mt-1 font-geist-mono text-[16px] text-primary">{value}</div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted">
        {formatDate(perf.startDate)} to {formatDate(perf.endDate)}. Google's data runs about two days behind.
      </p>

      <RankTable title="Top queries" rows={perf.queries.map((q) => ({ key: q.query, label: q.query, ...q }))} />
      <RankTable
        title="Top pages"
        rows={perf.pages.map((p) => ({ key: p.page, label: pagePath(p.page, siteUrl), ...p }))}
        mono
      />
    </>
  );
}

interface RankRow {
  key: string;
  label: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

const TH = 'px-2 py-2 text-right text-[10px] font-medium uppercase tracking-[0.12em] text-muted';

function RankTable({ title, rows, mono }: { title: string; rows: RankRow[]; mono?: boolean }) {
  return (
    <div className={classNames(CARD, 'overflow-x-auto')}>
      <table className="w-full text-[12px]">
        <thead>
          <tr className="border-b border-hairline">
            <th className="px-3 py-2 text-left text-[10px] font-medium uppercase tracking-[0.12em] text-muted">
              {title}
            </th>
            <th className={TH}>Clicks</th>
            <th className={TH}>Impr.</th>
            <th className={TH}>CTR</th>
            <th className={classNames(TH, 'pr-3')}>Pos.</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-hairline">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-3 py-4 text-center text-[11px] text-muted">
                No searches yet. New sites take a few days to show up.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={r.key}>
                <td
                  className={classNames(
                    'max-w-[12rem] truncate py-2 pl-3 pr-2 text-primary',
                    mono && 'font-geist-mono text-[11px]',
                  )}
                  title={r.key}
                >
                  {r.label}
                </td>
                <td className="px-2 py-2 text-right font-geist-mono text-secondary">{r.clicks}</td>
                <td className="px-2 py-2 text-right font-geist-mono text-secondary">{compactNumber(r.impressions)}</td>
                <td className="px-2 py-2 text-right font-geist-mono text-secondary">{ctrLabel(r.ctr)}</td>
                <td className="py-2 pl-2 pr-3 text-right font-geist-mono text-secondary">
                  {positionLabel(r.position)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
